import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useTelemetry } from "../context/TelemetryContext";
import AIActivityStream from "../components/operations/AIActivityStream";
import IncidentTimeline from "../components/operations/IncidentTimeline";
import RiskTrend from "../components/operations/RiskTrend";
import AIConfidenceGauge from "../components/operations/AIConfidenceGauge";
import AutonomousResponse from "../components/operations/AutonomousResponse";
import RegionalRiskMap from "../components/operations/RegionalRiskMap";
import LiveThreatAnalytics from "../components/operations/LiveThreatAnalytics";
import Sidebar from "../components/layout/Sidebar";
import LanguageToggle from "../components/shared/LanguageToggle";
import { getUserRole } from "../security/roleManager";
import { ROLES } from "../security/roles";
import API from "../config/api";

const PAID_ROLES = [
  ROLES.CUSTOMER_USER,
  ROLES.CUSTOMER_ADMIN,
  ROLES.FENTASAFE_ADMIN,
];

// Pre-populated demo memory incidents for realistic demo experience
const DEMO_MEMORY_INCIDENTS = [
  { pm25: 45, voc: 280, fentanyl_ppb: 3.2, timestamp: "09:15 AM", pattern: "ELEVATED" },
  { pm25: 72, voc: 340, fentanyl_ppb: 5.1, timestamp: "10:32 AM", pattern: "WARNING" },
  { pm25: 28, voc: 180, fentanyl_ppb: 0.8, timestamp: "11:45 AM", pattern: "SAFE" },
];

function AICommandCenter() {
  const { t } = useTranslation();
  const telemetry = useTelemetry();
  const role = getUserRole();
  const isLive = PAID_ROLES.includes(role);
  const isDemoUser = role === ROLES.DEMO_USER;

  const [result, setResult] = useState(null);
  const [logs, setLogs] = useState([]);
  const [riskHistory, setRiskHistory] = useState([]);
  const [sensorData, setSensorData] = useState({
    pm25: 42,
    voc: 220,
    fentanyl_ppb: 4,
  });

  // Auto-analyze when telemetry updates
  useEffect(() => {
    analyze();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    telemetry.pm25,
    telemetry.voc,
    telemetry.fentanyl_ppb,
    telemetry.overallRisk,
    telemetry.aiConfidence,
  ]);

  async function analyze() {
    // For live/paid users — call the real backend agent pipeline
    if (isLive) {
      try {
        const token = localStorage.getItem("FENTASAFE_TOKEN");
        const response = await fetch(`${API}/api/agent-risk`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            pm25: telemetry.pm25,
            voc: telemetry.voc,
            fentanyl_ppb: telemetry.fentanyl_ppb,
            temperature_f: 72,
            humidity_pct: 45,
          }),
        });

        if (response.ok) {
          const data = await response.json();
          setLogs(data.logs || []);
          setRiskHistory((prev) => [
            ...prev.slice(-9),
            {
              time: new Date().toLocaleTimeString(),
              risk: data.prediction?.risk,
              confidence: data.prediction?.confidence,
            },
          ]);
          setResult(data);
          return;
        }
      } catch (err) {
        console.warn("Live agent-risk API unavailable, falling back to telemetry context:", err);
      }
    }

    // For demo users OR live fallback — use TelemetryContext values
    // Compute dynamic AI risk factors from signal values
    const total = (telemetry.pm25 || 0) + (telemetry.voc || 0) / 10 + (telemetry.fentanyl_ppb || 0) * 5;
    const pm25Impact = total > 0 ? Math.round(((telemetry.pm25 || 0) / total) * 100) : 25;
    const vocImpact = total > 0 ? Math.round(((telemetry.voc || 0) / 10 / total) * 100) : 15;
    const fentImpact = Math.max(0, 100 - pm25Impact - vocImpact);
    // Derive from the same telemetry.explanations the Explanation panel renders,
    // instead of an independent impact-percentage comparison — otherwise the two
    // panels can name different signals as the cause of the current risk level.
    // Only "exceeded"-type reasons are valid drivers; when nothing has actually
    // exceeded a threshold (explanations holds "allNormal", "initializing", or
    // "liveDataActive"), say so rather than picking whichever signal's impact
    // percentage happens to be highest — that produced a false "X exceeded"
    // claim even while risk was SAFE and nothing had actually exceeded anything.
    const DRIVER_REASONS = ["pm25Exceeded", "vocExceeded", "exposureElevated"];
    const topExplanation = telemetry.explanations?.[0];
    const primaryDriver = DRIVER_REASONS.includes(topExplanation) ? topExplanation : "allNormal";

    // Fix: determine autonomous response status correctly
    const risk = telemetry.overallRisk || "SAFE";
    const autonomousStatus =
      risk === "CRITICAL"
        ? "emergencyResponse"
        : risk === "WARNING"
        ? "intervention"
        : "monitoring";
    const autonomousAction =
      risk === "CRITICAL"
        ? "immediateInterventionRequired"
        : risk === "WARNING"
        ? "escalateAndNotify"
        : "continueMonitoring";

    const demoData = {
      input: {
        pm25: telemetry.pm25,
        voc: telemetry.voc,
        fentanyl_ppb: telemetry.fentanyl_ppb,
      },
      prediction: {
        risk: telemetry.overallRisk,
        confidence: telemetry.aiConfidence,
      },
      decision: {
        actions:
          risk === "CRITICAL"
            ? ["immediateAlert", "notifySecurity", "escalateMonitoring"]
            : risk === "WARNING"
            ? ["increaseMonitoring", "notifyAdministrator", "prepareResponse"]
            : ["continueMonitoring", "maintainObservation"],
        autonomousStatus,
        autonomousAction,
      },
      explanation: {
        explanation: telemetry.explanations || [],
      },
      recommendation: {
        recommendations:
          risk === "CRITICAL"
            ? ["activateEmergencyProtocol", "initiateHazmatCheck", "notifyFacilityOfficer"]
            : risk === "WARNING"
            ? ["reviewTelemetry", "increaseMonitoringFrequency", "prepareIntervention"]
            : ["maintainNormalMonitoring", "reviewSensorCalibration"],
      },
      memory: {
        memory_count: DEMO_MEMORY_INCIDENTS.length,
        similar_events: DEMO_MEMORY_INCIDENTS.filter(
          (i) => Math.abs(i.pm25 - (telemetry.pm25 || 0)) < 30
        ),
        risk_pattern:
          DEMO_MEMORY_INCIDENTS.filter(
            (i) => Math.abs(i.pm25 - (telemetry.pm25 || 0)) < 30
          ).length > 0
            ? "REPEATING"
            : "ISOLATED",
      },
      learning: {
        critical_patterns: DEMO_MEMORY_INCIDENTS.filter(
          (i) => i.pattern === "WARNING" || i.pattern === "CRITICAL"
        ).length,
      },
      aiRiskFactors: {
        pm25Impact,
        vocImpact,
        fentImpact,
        primaryDriver,
      },
      logs: (telemetry.explanations || []).map((item) => ({
        time: new Date().toLocaleTimeString(),
        agent: "telemetryEngine",
        message: item,
      })),
    };

    setLogs(demoData.logs);
    setRiskHistory((prev) => [
      ...prev.slice(-9),
      {
        time: new Date().toLocaleTimeString(),
        risk: demoData.prediction.risk,
        confidence: demoData.prediction.confidence,
      },
    ]);
    setResult(demoData);
  }

  const analyticsData = riskHistory.map((item, index) => ({
    name: index + 1,
    confidence: item.confidence,
    risk:
      item.risk === "SAFE"
        ? 10
        : item.risk === "ELEVATED"
        ? 35
        : item.risk === "WARNING"
        ? 70
        : 95,
  }));

  const cardStyle = {
    background: "#0f172a",
    padding: "20px",
    borderRadius: "12px",
    border: "1px solid #1e293b",
    color: "#e2e8f0",
    boxShadow: "0px 2px 10px rgba(0,0,0,.4)",
  };

  const riskColor = (r) =>
    r === "SAFE"
      ? "#16a34a"
      : r === "ELEVATED"
      ? "#0ea5e9"
      : r === "WARNING"
      ? "#f59e0b"
      : "#dc2626";

  return (
    <div style={{ display: "flex", background: "#020617", minHeight: "100vh", fontFamily: "Arial", color: "#e2e8f0" }}>
      <Sidebar />
      <div style={{ marginLeft: "260px", width: "100%", padding: "20px" }}>

        {/* HEADER */}
        <div style={{ background: "#1e293b", color: "white", padding: "20px", borderRadius: "12px", marginBottom: "25px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div>
              <h1 style={{ margin: 0 }}>{t("commandCenter.title")}</h1>
              <p style={{ marginTop: "6px", color: "#94a3b8" }}>{t("commandCenter.subtitle")}</p>
            </div>
            <div style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
              <LanguageToggle />
              {/* MODE INDICATOR — Demo vs Live */}
              <div style={{
                padding: "8px 16px",
                borderRadius: "8px",
                background: isLive ? "rgba(22,163,74,0.15)" : "rgba(251,191,36,0.1)",
                border: `1px solid ${isLive ? "rgba(22,163,74,0.4)" : "rgba(251,191,36,0.3)"}`,
                textAlign: "center",
              }}>
                <div style={{ fontSize: "0.7rem", color: "#94a3b8", letterSpacing: "1px", textTransform: "uppercase" }}>
                  {t("commandCenter.mode")}
                </div>
                <div style={{ fontWeight: 700, color: isLive ? "#22c55e" : "#fbbf24", fontSize: "1rem" }}>
                  {isLive ? t("commandCenter.modeLive") : t("commandCenter.modeDemo")}
                </div>
                <div style={{ fontSize: "0.7rem", color: "#64748b" }}>
                  {isLive ? t("commandCenter.realSensorData") : t("commandCenter.simulatedData")}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* SENSOR INPUTS */}
        <div style={{ display: "flex", gap: "20px", marginBottom: "20px", flexWrap: "wrap", alignItems: "flex-end" }}>
          <div>
            <label style={{ fontSize: "0.85rem", color: "#94a3b8" }}>{t("commandCenter.sensorInputs.pm25")}</label>
            <br />
            <input
              type="number"
              value={sensorData.pm25}
              onChange={(e) => setSensorData({ ...sensorData, pm25: Number(e.target.value) })}
              style={{ padding: "8px", background: "#0f172a", border: "1px solid #1e293b", color: "#fff", borderRadius: "6px", width: 100 }}
            />
          </div>
          <div>
            <label style={{ fontSize: "0.85rem", color: "#94a3b8" }}>{t("commandCenter.sensorInputs.voc")}</label>
            <br />
            <input
              type="number"
              value={sensorData.voc}
              onChange={(e) => setSensorData({ ...sensorData, voc: Number(e.target.value) })}
              style={{ padding: "8px", background: "#0f172a", border: "1px solid #1e293b", color: "#fff", borderRadius: "6px", width: 100 }}
            />
          </div>
          <div>
            <label style={{ fontSize: "0.85rem", color: "#94a3b8" }}>{t("commandCenter.sensorInputs.exposureSignal")}</label>
            <br />
            <input
              type="number"
              value={sensorData.fentanyl_ppb}
              onChange={(e) => setSensorData({ ...sensorData, fentanyl_ppb: Number(e.target.value) })}
              style={{ padding: "8px", background: "#0f172a", border: "1px solid #1e293b", color: "#fff", borderRadius: "6px", width: 100 }}
            />
          </div>
          <button
            onClick={analyze}
            style={{ padding: "10px 24px", background: "#2563eb", color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: 600 }}
          >
            {t("commandCenter.sensorInputs.analyze")}
          </button>
        </div>

        {result && result.prediction && (
          <>
            {/* SENSOR SUMMARY */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: "20px", marginBottom: "20px" }}>
              <div style={cardStyle}><h3>{t("commandCenter.sensorInputs.pm25")}</h3><h1>{result?.input?.pm25}</h1></div>
              <div style={cardStyle}><h3>{t("commandCenter.sensorInputs.voc")}</h3><h1>{result?.input?.voc}</h1></div>
              <div style={cardStyle}><h3>{t("commandCenter.sensorInputs.exposureSignal")}</h3><h1>{result?.input?.fentanyl_ppb}</h1></div>
            </div>

            {/* RISK STATUS */}
            <div style={{ ...cardStyle, marginBottom: "20px" }}>
              <h1 style={{ color: riskColor(result?.prediction?.risk) }}>
                {result?.prediction?.risk === "SAFE" && `🟢 ${t("commandCenter.risk.SAFE")}`}
                {result?.prediction?.risk === "ELEVATED" && `🔵 ${t("commandCenter.risk.ELEVATED")}`}
                {result?.prediction?.risk === "WARNING" && `🟡 ${t("commandCenter.risk.WARNING")}`}
                {result?.prediction?.risk === "CRITICAL" && `🔴 ${t("commandCenter.risk.CRITICAL")}`}
              </h1>
              <h3>{t("commandCenter.riskStatus.confidence")}: <b>{result?.prediction?.confidence}%</b></h3>
              <progress value={result?.prediction?.confidence} max="100" style={{ width: "100%" }} />
            </div>

            {/* AI RESULT CARDS */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "20px", marginBottom: "20px" }}>

              <div style={cardStyle}>
                <h3>{t("commandCenter.cards.actions")}</h3>
                <ul>{result?.decision?.actions?.map((item, i) => <li key={i}>{t(`commandCenter.actionsList.${item}`)}</li>)}</ul>
              </div>

              <div style={cardStyle}>
                <h3>{t("commandCenter.cards.explanation")}</h3>
                <ul>{result?.explanation?.explanation?.length > 0
                  ? result.explanation.explanation.map((item, i) => <li key={i}>{t(`commandCenter.telemetryExplanations.${item}`, item)}</li>)
                  : <li>{t("commandCenter.cards.explanationFallback")}</li>}
                </ul>
              </div>

              <div style={cardStyle}>
                <h3>{t("commandCenter.cards.recommendations")}</h3>
                <ul>{result?.recommendation?.recommendations?.map((item, i) => <li key={i}>{t(`commandCenter.recommendationsList.${item}`)}</li>)}</ul>
              </div>

              <div style={cardStyle}>
                <h3>{t("commandCenter.cards.memoryIntelligence")}</h3>
                <p>{t("commandCenter.cards.storedEvents")}: <b>{result?.memory?.memory_count || 0}</b></p>
                <p>{t("commandCenter.cards.similarIncidents")}: <b>{result?.memory?.similar_events?.length || 0}</b></p>
                <p style={{ marginTop: 8, color: result?.memory?.risk_pattern === "REPEATING" ? "#f59e0b" : "#16a34a", fontWeight: 600 }}>
                  {t("commandCenter.cards.pattern")}: {t(`commandCenter.status.${(result?.memory?.risk_pattern || "ISOLATED").toLowerCase()}`)}
                </p>
              </div>

              <div style={cardStyle}>
                <h3>{t("commandCenter.cards.adaptiveLearning")}</h3>
                <p>{t("commandCenter.cards.criticalPatterns")}: <b>{result?.learning?.critical_patterns || 0}</b></p>
                <p style={{ marginTop: 8, color: "#94a3b8", fontSize: "0.85rem" }}>
                  {isLive
                    ? t("commandCenter.cards.operatorFeedbackLive")
                    : t("commandCenter.cards.operatorFeedbackDemo")}
                </p>
              </div>

              {/* AI Risk Factors — now computed dynamically */}
              <div style={cardStyle}>
                <h3>{t("commandCenter.cards.aiRiskFactors")}</h3>
                <p>{t("commandCenter.cards.pm25Impact")}: <b>{result?.aiRiskFactors?.pm25Impact ?? 75}%</b></p>
                <p>{t("commandCenter.cards.vocImpact")}: <b>{result?.aiRiskFactors?.vocImpact ?? 15}%</b></p>
                <p>{t("commandCenter.cards.exposureSignalImpact")}: <b>{result?.aiRiskFactors?.fentImpact ?? 10}%</b></p>
                <p style={{ marginTop: "10px", color: "#f59e0b", fontWeight: "bold" }}>
                  {t("commandCenter.cards.primaryDriver")}: {t(`commandCenter.primaryDriverText.${result?.aiRiskFactors?.primaryDriver ?? "allNormal"}`)}
                </p>
              </div>

            </div>

            {/* AI OPERATIONS PANELS */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(500px,1fr))", gap: "20px", marginTop: "20px" }}>

              <AIActivityStream logs={logs} cardStyle={cardStyle} />
              <IncidentTimeline logs={logs} cardStyle={cardStyle} />
              <RiskTrend riskHistory={riskHistory} cardStyle={cardStyle} />
              <AutonomousResponse result={result} cardStyle={cardStyle} />
              <AIConfidenceGauge result={result} cardStyle={cardStyle} isDemo={!isLive} />
              <RegionalRiskMap cardStyle={cardStyle} />

              {/* Threat Heat Index */}
              <div style={cardStyle}>
                <h3>{t("commandCenter.operationsPanels.threatHeatIndex")}</h3>
                <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span>{t("commandCenter.operationsPanels.low")}</span>
                    <span style={{ color: "#16a34a", fontWeight: "bold" }}>
                      {Math.max(0, 30 - (result?.prediction?.risk === "CRITICAL" ? 10 : 0))}
                    </span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span>{t("commandCenter.operationsPanels.medium")}</span>
                    <span style={{ color: "#f59e0b", fontWeight: "bold" }}>65</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span>{t("commandCenter.operationsPanels.high")}</span>
                    <span style={{ color: "#dc2626", fontWeight: "bold" }}>
                      {result?.prediction?.risk === "CRITICAL" ? 92 : result?.prediction?.risk === "WARNING" ? 45 : 12}
                    </span>
                  </div>
                </div>
              </div>

              {/* Digital Twin Forecast — now uses real prediction data */}
              <div style={cardStyle}>
                <h3>{t("commandCenter.operationsPanels.digitalTwinForecast")}</h3>
                <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                  <div><b>{t("commandCenter.operationsPanels.currentState")}:</b> {t(`commandCenter.risk.${result?.prediction?.risk || "SAFE"}`)}</div>
                  <div><b>{t("commandCenter.operationsPanels.predictedState")}:</b> {
                    t(`commandCenter.risk.${
                      result?.prediction?.risk === "ELEVATED" ? "WARNING" :
                      result?.prediction?.risk === "WARNING" ? "CRITICAL" :
                      result?.prediction?.risk === "CRITICAL" ? "CRITICAL" : "SAFE"
                    }`)
                  }</div>
                  <div><b>{t("commandCenter.operationsPanels.forecastWindow")}:</b> {t("commandCenter.operationsPanels.forecastWindowValue")}</div>
                  <div><b>{t("commandCenter.operationsPanels.confidence")}:</b> {result?.prediction?.confidence}%</div>
                  <div style={{
                    marginTop: "10px",
                    padding: "10px",
                    background: result?.prediction?.risk === "CRITICAL" ? "#fee2e2" : result?.prediction?.risk === "WARNING" ? "#fef3c7" : "#dcfce7",
                    borderRadius: "10px",
                    fontWeight: "bold",
                    color: "#0f172a",
                  }}>
                    {result?.prediction?.risk === "CRITICAL"
                      ? t("commandCenter.operationsPanels.escalationHigh")
                      : result?.prediction?.risk === "WARNING"
                      ? t("commandCenter.operationsPanels.escalationIncreasing")
                      : t("commandCenter.operationsPanels.escalationNone")}
                  </div>
                </div>
              </div>

              {/* Swarm Coordination */}
              <div style={cardStyle}>
                <h3>{t("commandCenter.operationsPanels.swarmCoordination")}</h3>
                <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                  <div><b>{t("commandCenter.operationsPanels.connectedZones")}:</b> {isLive ? t("commandCenter.operationsPanels.liveSensorNetwork") : t("commandCenter.operationsPanels.demoZones")}</div>
                  <div><b>{t("commandCenter.operationsPanels.sensorMesh")}:</b> {t("commandCenter.status.active")}</div>
                  <div><b>{t("commandCenter.operationsPanels.autonomousSync")}:</b> {t("commandCenter.status.enabled")}</div>
                  <div style={{ marginTop: "10px", padding: "10px", background: "#dcfce7", borderRadius: "10px", fontWeight: "bold", color: "#0f172a" }}>
                    {t("commandCenter.operationsPanels.distributedTelemetry")}
                  </div>
                </div>
              </div>

              {/* Geo-Spatial Overlay */}
              <div style={cardStyle}>
                <h3>{t("commandCenter.operationsPanels.geoSpatialOverlay")}</h3>
                <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                  <div><b>{t("commandCenter.operationsPanels.northSector")}:</b> <span style={{ color: riskColor("WARNING") }}>{t("commandCenter.risk.WARNING")}</span></div>
                  <div><b>{t("commandCenter.operationsPanels.eastSector")}:</b> <span style={{ color: riskColor("SAFE") }}>{t("commandCenter.risk.SAFE")}</span></div>
                  <div><b>{t("commandCenter.operationsPanels.southSector")}:</b> <span style={{ color: riskColor("ELEVATED") }}>{t("commandCenter.risk.ELEVATED")}</span></div>
                  <div><b>{t("commandCenter.operationsPanels.westSector")}:</b> <span style={{ color: riskColor("CRITICAL") }}>{t("commandCenter.risk.CRITICAL")}</span></div>
                </div>
              </div>

              {/* AI Governance */}
              <div style={cardStyle}>
                <h3>{t("commandCenter.operationsPanels.aiGovernance")}</h3>
                <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                  <div><b>{t("commandCenter.operationsPanels.explainability")}:</b> <span style={{ color: "#22c55e" }}>{t("commandCenter.status.verified")}</span></div>
                  <div><b>{t("commandCenter.operationsPanels.humanOversight")}:</b> <span style={{ color: "#22c55e" }}>{t("commandCenter.status.active")}</span></div>
                  <div><b>{t("commandCenter.operationsPanels.policyCompliance")}:</b> <span style={{ color: "#22c55e" }}>{t("commandCenter.status.enabled")}</span></div>
                  <div><b>{t("commandCenter.operationsPanels.auditLogging")}:</b> <span style={{ color: "#22c55e" }}>{t("commandCenter.status.active")}</span></div>
                  <div><b>{t("commandCenter.operationsPanels.dataSource")}:</b> <span style={{ color: isLive ? "#22c55e" : "#fbbf24" }}>{isLive ? t("commandCenter.status.liveSensors") : t("commandCenter.status.simulated")}</span></div>
                </div>
              </div>

              {/* Mission Status */}
              <div style={cardStyle}>
                <h3>{t("commandCenter.operationsPanels.missionStatus")}</h3>
                <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                  <div><b>{t("commandCenter.operationsPanels.operationalState")}:</b> {t("commandCenter.status.active")}</div>
                  <div><b>{t("commandCenter.operationsPanels.aiCoordination")}:</b> {t("commandCenter.status.stable")}</div>
                  <div><b>{t("commandCenter.operationsPanels.threatContainment")}:</b> {result?.prediction?.risk === "CRITICAL" ? t("commandCenter.status.inProgress") : t("commandCenter.status.nominal")}</div>
                  <div><b>{t("commandCenter.operationsPanels.modeLabel")}:</b> <span style={{ color: isLive ? "#22c55e" : "#fbbf24", fontWeight: 700 }}>{isLive ? t("commandCenter.status.liveOperations") : t("commandCenter.status.demonstration")}</span></div>
                  <div style={{ marginTop: "10px", padding: "10px", background: "#eff6ff", borderRadius: "10px", fontWeight: "bold", color: "#0f172a" }}>
                    {t("commandCenter.operationsPanels.commandInfraNormal")}
                  </div>
                </div>
              </div>

              <LiveThreatAnalytics analyticsData={analyticsData} cardStyle={cardStyle} />

            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default AICommandCenter;